import { useState, useEffect, useCallback } from 'react';
import { encryptText, decryptText } from '../lib/encryption';
import { supabase } from '../lib/supabase';

export function useEncryption() {
  const [userId, setUserId] = useState<string | null>(null);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (session?.user?.id) {
          setUserId(session.user.id);
          setIsReady(true);
          return true;
        }

        return false;
      } catch (err) {
        console.error('Session fetch error:', err);
        return false;
      }
    };

    const tryInitialize = async () => {
      const success = await initializeAuth();
      
      if (!success && retryCount < maxRetries && mounted) {
        retryCount++;
        setTimeout(tryInitialize, retryDelay);
      }
    };

    tryInitialize();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setUserId(session?.user?.id || null);
        setIsReady(!!session?.user?.id);
      } else if (event === 'SIGNED_OUT') {
        setUserId(null);
        setIsReady(false);
      }
    });

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const encrypt = useCallback(async (text: string) => {
    if (!isReady) {
      throw new Error('Encryption not ready - waiting for authentication');
    }

    setIsEncrypting(true);
    setError(null);

    try {
      if (!userId) {
        throw new Error('User ID required for encryption');
      }

      return await encryptText(text, userId);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsEncrypting(false);
    }
  }, [userId, isReady]);

  const decrypt = useCallback(async (text: string) => {
    if (!isReady) {
      return '[Waiting for authentication...]';
    }

    if (!text) {
      return '';
    }

    setIsDecrypting(true);
    setError(null);

    try {
      if (!userId) {
        throw new Error('User ID required for decryption');
      }

      // Check if the text appears to be encrypted
      // Base64 pattern and minimum length for encrypted content
      const isEncrypted = /^[A-Za-z0-9+/=]+$/.test(text) && text.length > 24;

      if (!isEncrypted) {
        console.log('Text appears unencrypted, returning as-is:', text);
        return text;
      }

      const result = await decryptText(text, userId);
      if (!result) {
        console.warn('Decryption returned null/undefined');
        return '[Decryption failed]';
      }

      return result;
    } catch (err: any) {
      console.error('Decryption error:', err);
      return '[Decryption failed]';
    } finally {
      setIsDecrypting(false);
    }
  }, [userId, isReady]);

  return {
    encrypt,
    decrypt,
    isEncrypting,
    isDecrypting,
    error,
    isReady,
    userId
  };
}